import {
  AVAILABLE_COLORS,
  PIVOT_GPS_JITTER_THRESHOLD_M,
  TRACTOR_GPS_JITTER_THRESHOLD_M,
  TRACTOR_STALE_THRESHOLD_S,
} from "common/utils/rtcJobs";
import { CenterPivot, Farm, Zone } from "protos/farm/farm";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DateTime } from "luxon";
import { DeviceLocationHistoryRecord } from "protos/rtc/device_location_history";
import { getSequence } from "common/utils/robots";
import { Job, Objective, ObjectiveAssignment, Task } from "protos/rtc/jobs";
import { LocationHistoryRecord } from "protos/rtc/location_history";
import distance from "@turf/distance";

export enum JobSelectionType {
  TASK = "task",
  ZONE = "zone",
  CENTER_PIVOT = "centerPivot",
  TRACTOR = "tractor",
  OBJECTIVE = "objective",
  JOB = "job",
}

interface ZoneSelection {
  type: JobSelectionType.ZONE;
  selection: Zone;
}
interface CenterPivotSelection {
  type: JobSelectionType.CENTER_PIVOT;
  selection: CenterPivot;
}
interface TractorSelection {
  type: JobSelectionType.TRACTOR;
  selection: { serial: string }; // we don't have a centralized autotractor entity, so we just store a serial
}
interface ObjectiveSelection {
  type: JobSelectionType.OBJECTIVE;
  selection: Objective;
}
interface TaskSelection {
  type: JobSelectionType.TASK;
  selection: Task;
}
interface JobSelection {
  type: JobSelectionType.JOB;
  selection: Job;
}

export type MapView = "objectives" | "robots" | "farm";

export type Selection =
  | ZoneSelection
  | CenterPivotSelection
  | TractorSelection
  | ObjectiveSelection
  | TaskSelection
  | JobSelection;

// identity of a selection, or undefined if it has none
const getSelectionKey = (s: Selection | undefined): string | undefined => {
  if (!s) {
    return;
  }
  const id =
    s.type === JobSelectionType.ZONE
      ? s.selection.id?.id
      : s.type === JobSelectionType.CENTER_PIVOT
      ? s.selection.endpointDeviceId
      : s.type === JobSelectionType.TRACTOR
      ? s.selection.serial
      : s.selection.id;
  return id ? `${s.type}:${id}` : undefined;
};

interface State {
  farm?: Farm;
  job?: Job;
  hiddenTractors: string[];
  highlightedObjectives: number[];
  assignmentsByTractorSerial: Record<string, ObjectiveAssignment[]>;
  selection?: Selection;
  focus?: Selection;
  colorsBySerial: Record<string, string>;
  liveTractorData: Record<string, LocationHistoryRecord>;
  liveDeviceData: Record<string, DeviceLocationHistoryRecord>;
  tractorIsMoving: Record<string, boolean>;
  tractorIsStale: Record<string, boolean>;
  deviceIsMoving: Record<string, boolean>;
  deviceIsStale: Record<string, boolean>;
  lastPolledTimestamp: string | null;
  mapView: MapView;
}

const initialState: State = {
  hiddenTractors: [],
  farm: undefined,
  job: undefined,
  assignmentsByTractorSerial: {},
  liveTractorData: {},
  liveDeviceData: {},
  lastPolledTimestamp: DateTime.now().toISO(),
  highlightedObjectives: [],
  selection: undefined,
  colorsBySerial: {},
  tractorIsMoving: {},
  tractorIsStale: {},
  deviceIsMoving: {},
  deviceIsStale: {},
  mapView: "objectives",
};

export const jobExplorer = createSlice({
  name: "jobExplorer",
  initialState,
  reducers: {
    setFarm(state, action: PayloadAction<Farm | undefined>) {
      state.farm = action.payload;
      state.selection = undefined;
      state.focus = undefined;
    },
    setJob(state, action: PayloadAction<Job | undefined>) {
      if (state.job?.id !== action.payload?.id) {
        state.selection = undefined;
        state.focus = undefined;
      }
      state.job = action.payload;
    },
    hideTractor(state, action: PayloadAction<string>) {
      state.hiddenTractors = [
        ...new Set([...state.hiddenTractors, action.payload]),
      ];
    },
    setHiddenTractors(state, action: PayloadAction<string[]>) {
      state.hiddenTractors = action.payload;
    },
    setMapView(state, action: PayloadAction<MapView>) {
      state.mapView = action.payload;
    },
    setSelection(state, action: PayloadAction<Selection | undefined>) {
      state.selection = action.payload;

      // set highlighted objectives when a tractor is selected
      if (action.payload?.type === "tractor") {
        state.highlightedObjectives =
          state.assignmentsByTractorSerial[
            action.payload.selection.serial
          ]?.map((o) => o.objectiveId) ?? [];
      } else {
        state.highlightedObjectives = [];
      }
    },
    setAssignmentsByTractorSerial(
      state,
      action: PayloadAction<Record<string, ObjectiveAssignment[]>>
    ) {
      state.assignmentsByTractorSerial = action.payload;
    },
    setFocus(state, action: PayloadAction<Selection | undefined>) {
      // hover dispatches this on every mousemove; keep the state reference
      // when the focused entity hasn't changed so subscribers don't re-render
      const key = getSelectionKey(action.payload);
      if (key !== undefined && key === getSelectionKey(state.focus)) {
        return;
      }
      state.focus = action.payload;
    },
    setTractorColor(
      state,
      action: PayloadAction<{ serial: string; color: string }>
    ) {
      state.colorsBySerial[action.payload.serial] = action.payload.color;
    },
    setHighlightedObjectives(
      state,
      action: PayloadAction<{ objectiveIds: number[] }>
    ) {
      state.highlightedObjectives = action.payload.objectiveIds;
    },
    setTractorStale(
      state,
      action: PayloadAction<{ serial: string; data: boolean }>
    ) {
      state.tractorIsStale[action.payload.serial] = action.payload.data;
    },
    setTractorMoving(
      state,
      action: PayloadAction<{ serial: string; data: boolean }>
    ) {
      state.tractorIsMoving[action.payload.serial] = action.payload.data;
    },
    setDeviceStale(
      state,
      action: PayloadAction<{ deviceId: string; data: boolean }>
    ) {
      state.deviceIsStale[action.payload.deviceId] = action.payload.data;
    },
    setDeviceMoving(
      state,
      action: PayloadAction<{ deviceId: string; data: boolean }>
    ) {
      state.deviceIsStale[action.payload.deviceId] = action.payload.data;
    },
    setTractorData: {
      prepare: ({
        serial,
        data,
      }: {
        serial: string;
        data: LocationHistoryRecord;
      }) => {
        return {
          payload: {
            serial,
            data,
            now: DateTime.now().toSeconds(),
          },
        };
      },
      reducer: (
        state,
        action: PayloadAction<{
          serial: string;
          data: LocationHistoryRecord;
          now: number;
        }>
      ) => {
        const prevTractorData = state.liveTractorData[action.payload.serial];
        if (action.payload.data.point && prevTractorData?.point) {
          state.tractorIsMoving[action.payload.serial] =
            distance(
              [action.payload.data.point.lng, action.payload.data.point.lat],
              [prevTractorData.point.lng, prevTractorData.point.lat],
              { units: "meters" }
            ) > TRACTOR_GPS_JITTER_THRESHOLD_M;
        } else {
          state.tractorIsMoving[action.payload.serial] = true;
        }
        if (
          action.payload.data.timestamp &&
          action.payload.now -
            DateTime.fromISO(action.payload.data.timestamp).toSeconds() <
            TRACTOR_STALE_THRESHOLD_S
        ) {
          // only un-stale when timestamp is within threshold.
          state.tractorIsStale[action.payload.serial] = false;
        }

        state.liveTractorData[action.payload.serial] = action.payload.data;

        // if we don't have a color for this tractor, assign it a random one
        if (!(action.payload.serial in state.colorsBySerial)) {
          const i =
            getSequence(action.payload.serial) % AVAILABLE_COLORS.length;
          state.colorsBySerial[action.payload.serial] =
            AVAILABLE_COLORS[i] ?? "#FF0000";
        }
      },
    },
    setDeviceData(
      state,
      action: PayloadAction<{
        deviceId: string;
        data: DeviceLocationHistoryRecord;
      }>
    ) {
      const prevDeviceData = state.liveDeviceData[action.payload.deviceId];
      if (action.payload.data.point && prevDeviceData?.point) {
        state.deviceIsMoving[action.payload.deviceId] =
          distance(
            [action.payload.data.point.lng, action.payload.data.point.lat],
            [prevDeviceData.point.lng, prevDeviceData.point.lat],
            { units: "meters" }
          ) > PIVOT_GPS_JITTER_THRESHOLD_M;
      } else {
        state.deviceIsMoving[action.payload.deviceId] = true;
      }
      state.deviceIsStale[action.payload.deviceId] = false;
      state.liveDeviceData[action.payload.deviceId] = action.payload.data;
    },
    setLastPolled(state, action: PayloadAction<DateTime>) {
      state.lastPolledTimestamp = action.payload.toISO();
    },
  },
  selectors: {
    selectJob: (state) => state.job,
    selectFarm: (state) => state.farm,
    selectFarmId: (state) => state.farm?.id?.id,
    selectHiddenTractors: (state) => state.hiddenTractors,
    selectHighlightedObjectives: (state) => state.highlightedObjectives,
    selectMapView: (state) => state.mapView,
    selectAssignmentsByTractorSerial: (state) =>
      state.assignmentsByTractorSerial,
    selectColorsBySerial: (state) => state.colorsBySerial,
    selectLiveTractorData: (state) => state.liveTractorData,
    selectSelection: (state) => state.selection,
    selectFocus: (state) => state.focus,
    // Memoized selector that only updates when the specific tractor's data changes
    selectTractorDataBySerials: createSelector(
      [
        (state: State) => state.colorsBySerial,
        (state: State) => state.liveTractorData,
        (state: State) => state.tractorIsMoving,
        (state: State) => state.tractorIsStale,
        (_state: State, serials: string[]) => serials,
      ],
      (
        colorsBySerial,
        liveTractorData,
        tractorIsMoving,
        tractorIsStale,
        serials
      ) =>
        Object.fromEntries(
          serials.map((serial) => [
            serial,
            {
              color: colorsBySerial[serial],
              liveTractorData: liveTractorData[serial],
              tractorIsMoving: tractorIsMoving[serial],
              tractorIsStale: tractorIsStale[serial],
            },
          ])
        )
    ),
    selectEndpointDeviceDataByDeviceIds: createSelector(
      [
        (state: State) => state.liveDeviceData,
        (state: State) => state.deviceIsMoving,
        (state: State) => state.deviceIsStale,
        (_state: State, deviceIds: string[]) => deviceIds,
      ],
      (liveDeviceData, deviceIsMoving, deviceIsStale, deviceIds) =>
        Object.fromEntries(
          deviceIds.map((deviceId) => [
            deviceId,
            {
              liveDeviceData: liveDeviceData[deviceId],
              deviceIsMoving: deviceIsMoving[deviceId],
              deviceIsStale: deviceIsStale[deviceId],
            },
          ])
        )
    ),
    // memoized selector that only updates when the selection changes
    selectShowDetails: createSelector(
      [(state: State) => state.selection],
      (selection) => selection !== undefined
    ),
  },
});
